import { z } from 'zod';

/**
 * Consumo de Cloud Storage por tenant — `tenants/{tenantId}/usage/storage`.
 *
 * Mantido por deltas pelos triggers de Storage (`apps/functions/src/storage/
 * storageUsage.ts`): upload soma, remoção subtrai. Não há tabela por arquivo —
 * o GCS não tem API que devolva o tamanho de um prefixo, e somar os tamanhos
 * que cada evento já carrega é o mais perto disso que existe.
 *
 * O bucket tem versionamento (lifecycle apaga versão antiga em 90 dias), então
 * são dois totais: `live*` é o que o tenant "tem"; `noncurrent*` é o que já
 * foi apagado/substituído e continua ocupando (e sendo cobrado) até o
 * lifecycle limpar. `byKind` quebra só o `live`.
 */

export const STORAGE_KINDS = ['IMAGE', 'AUDIO', 'VIDEO', 'PDF', 'OTHER'] as const;
export const StorageKindSchema = z.enum(STORAGE_KINDS);
export type StorageKind = z.infer<typeof StorageKindSchema>;

/** Classifica pelo `contentType` gravado no objeto (ausente → `OTHER`). */
export function storageKindFromContentType(contentType: string | null | undefined): StorageKind {
  const ct = (contentType ?? '').toLowerCase();
  if (ct.startsWith('image/')) return 'IMAGE';
  if (ct.startsWith('audio/')) return 'AUDIO';
  if (ct.startsWith('video/')) return 'VIDEO';
  if (ct === 'application/pdf') return 'PDF';
  return 'OTHER';
}

/**
 * Inteiro (pode ficar negativo por um instante: o `archived` de um objeto
 * pode chegar antes do `finalized` dele — soma é comutativa, o total
 * converge). Por isso não é `nonnegative()`.
 */
const Counter = z.number().int();

const KindUsageSchema = z.object({ bytes: Counter, objects: Counter });

export const TenantStorageUsageSchema = z.object({
  tenantId: z.string().min(1),
  liveBytes: Counter,
  liveObjects: Counter,
  noncurrentBytes: Counter,
  noncurrentObjects: Counter,
  byKind: z.object({
    IMAGE: KindUsageSchema,
    AUDIO: KindUsageSchema,
    VIDEO: KindUsageSchema,
    PDF: KindUsageSchema,
    OTHER: KindUsageSchema,
  }).partial(),
});
export type TenantStorageUsage = z.infer<typeof TenantStorageUsageSchema>;

/** Id do doc em `tenants/{tid}/usage/{docId}`. */
export const TENANT_STORAGE_USAGE_DOC_ID = 'storage';
